import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from '../../../../utils/translation-utils';
import './with-input-validation.scss';
import { getMultipleValidations } from '../../../../utils/validation-util';
import AnimateHeight from '../../../views/animate-height/animate-height';
import { DefaultRegexMessageMap } from '../../../../constants/regex';
import { InputTestIds } from '../../../views/input';

const VALIDATION_DELAY = 1000;

/* eslint-disable react/jsx-props-no-spreading */
const withInputValidation = (View) => {
  const WithValidation = React.forwardRef(({
    validations, validationDetails, forcedErrorMessages, value, label, onChange, className,
    isSubmit, validateOnSubmit, onBlur, showForcedMsgWithDelay, regexMessageMap, testIds, ...props
  }, ref) => {
    const timer = useRef();
    const [getTranslation] = useTranslation();
    const translationData = {
      label: label || getTranslation('THIS'),
      ...validationDetails,
    };
    const [shouldValidate, setShouldValidate] = useState(isSubmit);
    const [errorMessages, setErrorMessages] = React.useState([]);

    useEffect(() => {
      setShouldValidate(isSubmit);
    }, [isSubmit]);

    // show forced error message with delay only when showForcedMsgWithDelay = true
    useEffect(() => {
      if (!showForcedMsgWithDelay || shouldValidate) {
        setErrorMessages(forcedErrorMessages);
      }
    }, [shouldValidate, forcedErrorMessages]);

    const handleOnChange = (event) => {
      onChange.call(null, event);
    };

    const handleKeyUp = () => {
      if (!isSubmit && validateOnSubmit) {
        return;
      }
      setShouldValidate(false);
      if (showForcedMsgWithDelay) {
        setErrorMessages([]);
      }
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        clearTimeout(timer.current);
        setShouldValidate(true);
      }, VALIDATION_DELAY);
    };

    const handleOnBlur = (event) => {
      onBlur?.call(null, event);
      if (!isSubmit && validateOnSubmit) {
        return;
      }
      setShouldValidate(true);
    };

    const getView = () => {
      const regexMap = { ...DefaultRegexMessageMap, ...regexMessageMap };
      const failedValidations = shouldValidate
        ? getMultipleValidations(value, validations, validationDetails, regexMap) : [];
      const totalErrorCount = failedValidations.length + errorMessages.length;

      const listItems = failedValidations.map((validation) => (
        <li key={validation}>
          {getTranslation(regexMap[validation].message, translationData)}
        </li>
      ));

      return (
        <div className={clsx('hoc-with-input-val-cont', className, { 'multiple-val': totalErrorCount > 1 })}>
          <View
            ref={ref}
            value={value}
            label={label}
            isInvalid={!!totalErrorCount}
            onChange={handleOnChange}
            onBlur={handleOnBlur}
            onKeyUp={handleKeyUp}
            testIds={testIds}
            {...props}
          />
          <AnimateHeight>
            {totalErrorCount > 0 && (
              <div className="hoc-wiv-footer" data-testid={testIds[InputTestIds.ERROR]}>
                <ul>
                  {listItems}
                  {errorMessages.map(
                    (msg) => <li key={msg}>{msg}</li>,
                  )}
                </ul>
              </div>
            )}
          </AnimateHeight>
        </div>
      );
    };

    return getView();
  });

  WithValidation.propTypes = {
    validations: PropTypes.arrayOf(PropTypes.string),
    validationDetails: PropTypes.shape({}),
    forcedErrorMessages: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    className: PropTypes.string,
    isSubmit: PropTypes.bool,
    validateOnSubmit: PropTypes.bool,
    regexMessageMap: PropTypes.shape({}),
    showForcedMsgWithDelay: PropTypes.bool,
    testIds: PropTypes.shape({}),
  };

  WithValidation.defaultProps = {
    validations: [],
    validationDetails: {},
    forcedErrorMessages: [],
    regexMessageMap: {},
    label: '',
    value: '',
    onChange: () => {},
    onBlur: () => {},
    className: '',
    isSubmit: false,
    validateOnSubmit: false,
    showForcedMsgWithDelay: false,
    testIds: {},
  };

  return WithValidation;
};

export default withInputValidation;
